import {
  getAnonymityModeKeyForShowingAnonymityInfo,
  HeadingLarge,
  useClickOutside,
  useIsVisible,
} from '@arnold/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { ArchivedSection, CenterView } from '../../components';
import {
  ReportSequenceSubscriptionSubscription,
  ReportSubscriptionSubscription,
  SurveyStatus,
  TopicGroupTypeCode,
} from '../../generated/hooks';
import { composeSearchParamsString } from '../../lib/common';
import { Filter, getPrintableReportMonth, getTopicGroupTranslation } from '../../lib/reportHelper';
import { aggregateDataForReportSequenceDetail } from '../../lib/reportSequenceHelper';
import { AggregatedData, Team } from '../../lib/reports/aggregateData';
import { AggregatedReportDataContext } from '../../lib/reports/aggregatedReportDataContext';
import { PageTitleAndFavicon } from '../../utils';
import Contacts from '../components/Contacts';
import Footer from '../components/Footer';
import GlobalSequenceFilters from '../components/GlobalSequenceFilters';
import { MobileFilters } from '../components/MobileFilters';
import { NavbarFilter } from '../components/NavbarFilter';
import { PhasesSection } from '../components/PhasesSection';
import { PhasesSectionMobile } from '../components/PhasesSectionMobile';
import ReportContainer from '../components/ReportContainer';
import { ReportMetrics } from '../components/ReportMetrics';
import ReportSection from '../components/ReportSection';
import { useIsMobileView } from '../components/ResponsiveSwitcher';
import { SideMenu, SideMenuContextProvider } from '../components/SideMenu';
import { FilterModal } from '../components/StyledComponents';
import { useMetadataSetter, useSendOpenReportToIntercom } from '../components/hooks';
import { buildReportGTMDimensions, ReportGTMContext } from '../ReportGTMContext';
import ConslusionTeams from './ConclusionTeams';
import IntroWidgets from './IntroWidgets';
import ParticipantTable from './ParticipantTable';
import { usePageParam } from '../../lib/reports/reportUtils';

type ReportSequenceData = NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>;

const getOverallProcessAnonymityModeKey = (
  reportSequence: ReportSequenceData,
): 'anonymousWithNonAnonymousOption' | 'nonAnonymousWithAnonymousOption' | 'anonymous' | 'underName' => {
  const stepModeKeys = (reportSequence.surveyGroup?.steps ?? [])
    .map((s) => s.processStep?.stepTopicGroup)
    .filter(Boolean)
    .map((tg: any) =>
      getAnonymityModeKeyForShowingAnonymityInfo(tg.defaultAnonymity ?? null, tg.allowedAnonymities ?? []),
    );

  if (stepModeKeys.includes('anonymous')) return 'anonymous';
  if (stepModeKeys.includes('anonymousWithNonAnonymousOption')) return 'anonymousWithNonAnonymousOption';
  if (stepModeKeys.includes('nonAnonymousWithAnonymousOption')) return 'nonAnonymousWithAnonymousOption';
  if (stepModeKeys.length > 0) return 'underName';

  // If steps aren't present for some reason, fall back to the parent topic group settings.
  return getAnonymityModeKeyForShowingAnonymityInfo(
    reportSequence.surveyGroup?.topicGroup?.defaultAnonymity ?? null,
    reportSequence.surveyGroup?.topicGroup?.allowedAnonymities ?? [],
  );
};

interface IReportSequenceProps extends RouteComponentProps<any> {
  accessToken: string;
  selectedLanguage: string;
  changeLanguage: (lang: string) => any;
  setReportRespondent?: (
    respondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'],
  ) => void;
  setAllowedLanguages: (languages: string[]) => void;
  allowedLanguages?: string[];
  reportSequence: ReportSequenceData;
  publicAccess?: boolean;
  reloading?: boolean;
  setTeamsFilter: (teams: string[]) => void;
  teamsFilter: string[];
  usedTeamsFilter: string[] | null;
  addedOnFrom: string | null | undefined;
  addedOnTo: string | null | undefined;
  setAddedOnFrom: (newValue: string | null) => void;
  setAddedOnTo: (newValue: string | null) => void;
  setAddedOnAndLoad: (from: string | null, to: string | null) => void;
  onConfirm: (shoudReload?: boolean) => void;
  setNavbarContent?: (content: React.ReactNode) => void;
  navbarContent?: React.ReactNode;
  usedAddedOnFrom: string | null | undefined;
  usedAddedOnTo: string | null | undefined;
  filters: Filter[];
  setFilters: (filters: Filter[]) => void;
  previousHiringInfo: ReportSequenceData['hiringInfo'];
  showOnlyUnread?: boolean;
  setShowOnlyUnread: (value: boolean) => void;
}

const ReportSequence = ({
  accessToken,
  previousHiringInfo,
  reportSequence,
  selectedLanguage,
  changeLanguage,
  reloading,
  setReportRespondent,
  location,
  history,
  setAllowedLanguages,
  allowedLanguages,
  publicAccess,
  addedOnFrom,
  addedOnTo,
  usedAddedOnFrom,
  usedAddedOnTo,
  teamsFilter,
  usedTeamsFilter,
  setAddedOnFrom,
  setAddedOnTo,
  setAddedOnAndLoad,
  setTeamsFilter,
  onConfirm,
  setNavbarContent,
  filters,
  setFilters,
  setShowOnlyUnread,
  showOnlyUnread,
}: IReportSequenceProps) => {
  const { t } = useTranslation(['Report', 'header']);
  const isMobile = useIsMobileView();
  const [currentPage] = usePageParam();
  useMetadataSetter(
    changeLanguage,
    selectedLanguage,
    reportSequence.metadata.language && reportSequence.metadata.language.code,
    reportSequence.metadata.respondent as NonNullable<
      ReportSubscriptionSubscription['getReport']['metadata']
    >['respondent'],
    setReportRespondent,
  );
  const ref = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [showFilters, setShowFilters] = useState(false);

  useClickOutside([modalRef, buttonRef, ref], setShowFilters);

  const handleVisibility = useCallback(
    (isVisible: boolean) => {
      if (!isVisible || (showFilters && isMobile)) {
        setNavbarContent &&
          setNavbarContent(
            <NavbarFilter
              pillNumber={(usedAddedOnFrom || usedAddedOnTo ? 1 : 0) + (usedTeamsFilter?.length ? 1 : 0)}
              title={topicGroupTranslation.value}
              addedOnFrom={usedAddedOnFrom}
              addedOnTo={usedAddedOnTo}
              setShowFilters={setShowFilters}
              showFilters={showFilters}
              compact={isMobile}
              ref={buttonRef}
            />,
          );
      } else {
        setNavbarContent && setNavbarContent(null);
        if (!isMobile) {
          setShowFilters(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usedAddedOnFrom, usedAddedOnTo, usedTeamsFilter, showFilters, isMobile],
  );

  useIsVisible(ref, handleVisibility);

  const params = new URLSearchParams(location.search);

  useEffect(() => {
    return () => {
      setNavbarContent && setNavbarContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSendOpenReportToIntercom(!!publicAccess, reportSequence.metadata.respondent);

  useEffect(() => {
    if (reportSequence.teams?.length) {
      setAggregatedDataForReport(aggregateDataForReportSequenceDetail(reportSequence.teams));
    }
  }, [reportSequence]);

  useEffect(() => {
    if (
      (!reportSequence.metadata.language?.code || reportSequence.metadata.language.code === 'en') &&
      (!allowedLanguages || !allowedLanguages.length)
    ) {
      setAllowedLanguages(['en']);
    } else if (reportSequence.metadata.language?.code && reportSequence.metadata.language.code !== 'en') {
      setAllowedLanguages([reportSequence.metadata.language.code, 'en']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSequence]);

  const [aggregatedDataForReport, setAggregatedDataForReport] = useState<AggregatedData | null>(null);

  const onFilterConfirm = () => {
    onConfirm(true);
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleUnitTeamChange = (teams: string[]) => {
    if (teams.length) {
      params.set('unit-teams', teams.join(',') || '');
    } else {
      params.delete('unit-teams');
    }
    setTeamsFilter(teams);
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const rootTeams: Team[] = reportSequence.reportRoots
    .map((rootTeamId) => aggregatedDataForReport?.teams[rootTeamId]?.team)
    .filter(Boolean) as Team[];

  const menuSections = [
    { id: 'intro', headingLocal: t('intro') },
    { id: 'topics', headingLocal: t('topics') },
    { id: 'about', headingLocal: t('about') },
  ];
  if (reportSequence?.surveyGroup?.topicGroup && !reportSequence?.surveyGroup?.topicGroup.hideOnboardingParts) {
    // insert on third position into menuSections
    const isHiring = reportSequence.surveyGroup.topicGroup.typeCode === TopicGroupTypeCode.Hiring;
    menuSections.splice(2, 0, {
      id: 'respondentsTable',
      headingLocal: isHiring ? t('respondentsHiringTable') : t('respondentsTable'),
    });
  }

  if (!reportSequence.surveyGroup) {
    return null;
  }

  const topicGroupTranslation = getTopicGroupTranslation(
    reportSequence.surveyGroup.topicGroup,
    reportSequence.metadata.language,
  );

  const positionIds = filters.find((filter) => filter.operator === 'position')?.values?.filter(Boolean) ?? [];
  const recruiterIds = filters.find((filter) => filter.operator === 'recruiter')?.values?.filter(Boolean) ?? [];
  const managerIds = filters.find((filter) => filter.operator === 'manager')?.values?.filter(Boolean) ?? [];

  const sequenceSearchParams = {
    added_on_from: addedOnFrom,
    added_on_to: addedOnTo,
    'unit-teams': teamsFilter.join(','),
    position: positionIds.join(','),
    recruiter: recruiterIds.join(','),
    manager: managerIds.join(','),
    unread_responses: showOnlyUnread ? 'true' : undefined,
    page: currentPage,
  };

  const subReportSearchParams = {
    ...sequenceSearchParams,
    position_ids: positionIds.join(','),
    recruiter_ids: recruiterIds.join(','),
    manager_ids: managerIds.join(','),
    position: positionIds
      .map((id) => reportSequence.hiringInfo?.positions?.find((item) => id === item.id)?.label)
      .filter(Boolean)
      .join(','),
    recruiter: recruiterIds
      .map((id) => reportSequence.hiringInfo?.recruiters?.find((item) => id === item.id)?.label)
      .filter(Boolean)
      .join(','),
    manager: managerIds
      .map((id) => reportSequence.hiringInfo?.managers?.find((item) => id === item.id)?.label)
      .filter(Boolean)
      .join(','),
  };

  const isAllDataAvailable = reportSequence.rootTeamsStatistics && !reloading;
  const reportIsNotEmpty =
    isAllDataAvailable && reportSequence.rootTeamsStatistics?.some((st) => st.statistics.responseRate.denominator > 0);

  const gtmDimensions = buildReportGTMDimensions({
    organizationId: reportSequence.metadata.organization.id,
    organizationName: reportSequence.metadata.organization.name,
    reportName: topicGroupTranslation.value,
    reportType: 'Onboarding',
  });

  return (
    <ReportGTMContext.Provider value={gtmDimensions}>
      <SideMenuContextProvider sections={menuSections}>
        <AggregatedReportDataContext.Provider value={aggregatedDataForReport}>
          <ReportContainer
            date={`${getPrintableReportMonth(
              reportSequence,
              reportSequence.metadata.language && reportSequence.metadata.language.code,
            )}`}
            anonymityMode={getOverallProcessAnonymityModeKey(reportSequence)}
            rootTeams={rootTeams.filter((rootTeam) => {
              if (isAllDataAvailable && reportSequence.rootTeamsStatistics) {
                if (!reportIsNotEmpty) {
                  return true;
                }
                const stats = reportSequence.rootTeamsStatistics.find((st) => st.teamId === rootTeam.id);
                if (stats) {
                  return stats.statistics.responseRate.denominator > 0;
                }
              }
              return false;
            })}
            title={topicGroupTranslation.value}
            about={topicGroupTranslation.description!}
            breadcrumb={[{ title: topicGroupTranslation.value }]}
            organizationName={reportSequence.metadata.organization.name}
            respondentName={
              reportSequence.metadata?.respondent
                ? `${reportSequence.metadata.respondent.firstname} ${reportSequence.metadata.respondent.surname}`
                : undefined
            }
          >
            <>
              {reportSequence.metadata.status !== SurveyStatus.Archived ? (
                <section className={'section'}>
                  <FilterModal showFilters={showFilters}>
                    <GlobalSequenceFilters
                      typeCode={reportSequence.surveyGroup.topicGroup.typeCode}
                      sequenceStart={new Date(reportSequence.surveyGroup.from)}
                      sequenceEnd={new Date(reportSequence.surveyGroup.to)}
                      onAddedOnFromChange={setAddedOnFrom}
                      onAddedOnToChange={setAddedOnTo}
                      setAddedOnAndLoad={setAddedOnAndLoad}
                      respondentId={reportSequence.metadata.respondent?.id}
                      allRespondentsCount={reportSequence.totalRespondents ?? 0}
                      filteredRespondentsCount={reportSequence.filteredRespondents ?? 0}
                      rootTeamsIds={reportSequence.reportRoots}
                      aggregatedData={aggregatedDataForReport}
                      setTeamsFilter={handleUnitTeamChange}
                      addedOnFrom={addedOnFrom}
                      addedOnTo={addedOnTo}
                      teamsFilter={teamsFilter}
                      onConfirm={onFilterConfirm}
                      loading={!isAllDataAvailable}
                      ref={modalRef}
                      hiringInfo={reportSequence.hiringInfo ?? previousHiringInfo}
                      filters={filters}
                      setFilters={setFilters}
                      organizationId={reportSequence.metadata.organization.id}
                    />
                  </FilterModal>
                  {publicAccess && <ReportMetrics reportAccessKey={accessToken} trackTime />}
                  <div className={'container container--md position-relative'}>
                    {!showFilters && <SideMenu sections={menuSections} />}
                    {isMobile ? (
                      <MobileFilters
                        setShowFilters={setShowFilters}
                        pillNumber={(usedAddedOnFrom || usedAddedOnTo ? 1 : 0) + (usedTeamsFilter?.length ? 1 : 0)}
                        addedOnFrom={usedAddedOnFrom}
                        addedOnTo={usedAddedOnTo}
                        ref={ref}
                        isInSequence
                      />
                    ) : (
                      <GlobalSequenceFilters
                        typeCode={reportSequence.surveyGroup.topicGroup.typeCode}
                        sequenceStart={new Date(reportSequence.surveyGroup.from)}
                        sequenceEnd={new Date(reportSequence.surveyGroup.to)}
                        onAddedOnFromChange={setAddedOnFrom}
                        onAddedOnToChange={setAddedOnTo}
                        setAddedOnAndLoad={setAddedOnAndLoad}
                        respondentId={reportSequence.metadata.respondent?.id}
                        allRespondentsCount={reportSequence.totalRespondents ?? 0}
                        filteredRespondentsCount={reportSequence.filteredRespondents ?? 0}
                        rootTeamsIds={reportSequence.reportRoots}
                        aggregatedData={aggregatedDataForReport}
                        setTeamsFilter={handleUnitTeamChange}
                        addedOnFrom={addedOnFrom}
                        addedOnTo={addedOnTo}
                        teamsFilter={teamsFilter}
                        onConfirm={onFilterConfirm}
                        loading={!isAllDataAvailable}
                        ref={ref}
                        classNames={'mb-9'}
                        hiringInfo={reportSequence.hiringInfo ?? previousHiringInfo}
                        filters={filters}
                        setFilters={setFilters}
                        organizationId={reportSequence.metadata.organization.id}
                      />
                    )}
                    {isAllDataAvailable && reportSequence.surveyGroup && reportSequence.widgets && (
                      <IntroWidgets
                        surveyGroup={reportSequence.surveyGroup}
                        metadata={reportSequence.metadata}
                        widgets={reportSequence.widgets}
                      />
                    )}
                    {!isAllDataAvailable && (
                      <CenterView>
                        <HeadingLarge>{t('ReportSequence:loading')}</HeadingLarge>
                        <p>{t('ReportSequence:loadingDescription')}</p>

                        <LoadingAnimation />
                      </CenterView>
                    )}
                    {isAllDataAvailable && (
                      <ReportSection id={'topics'} header={t('topics')} icomTag={'process-report-topics-title'}>
                        {isMobile ? (
                          <PhasesSectionMobile
                            reportSequence={reportSequence}
                            getTopicUrl={(topicId) =>
                              `/${publicAccess ? 'results' : 'report'}/${accessToken}${composeSearchParamsString({
                                topic_group_id: topicId,
                                phasesChecked: params.get('phasesChecked'),
                                phasesActiveTab: params.get('phasesActiveTab'),
                                timeRangePeriod1: params.get('timeRangePeriod1'),
                                timeRangePeriod2: params.get('timeRangePeriod2'),
                                timeRangePeriod3: params.get('timeRangePeriod3'),
                                ...subReportSearchParams,
                              })}`
                            }
                          />
                        ) : (
                          <PhasesSection
                            accessToken={accessToken}
                            reportSequence={reportSequence}
                            teamsFilter={teamsFilter}
                            getTopicUrl={(topicId) =>
                              `/${publicAccess ? 'results' : 'report'}/${accessToken}${composeSearchParamsString({
                                topic_group_id: topicId,
                                phasesChecked: params.get('phasesChecked'),
                                phasesActiveTab: params.get('phasesActiveTab'),
                                timeRangePeriod1: params.get('timeRangePeriod1'),
                                timeRangePeriod2: params.get('timeRangePeriod2'),
                                timeRangePeriod3: params.get('timeRangePeriod3'),
                                ...subReportSearchParams,
                              })}`
                            }
                          />
                        )}
                      </ReportSection>
                    )}
                    {isAllDataAvailable && !reportSequence.surveyGroup.topicGroup.hideOnboardingParts && (
                      <ParticipantTable
                        accessToken={accessToken}
                        reportSequence={reportSequence}
                        publicAccess={publicAccess}
                        teamsFilter={usedTeamsFilter}
                        urlParams={composeSearchParamsString(sequenceSearchParams)}
                        onReload={onConfirm}
                        filters={filters}
                        showOnlyUnread={showOnlyUnread}
                        setShowOnlyUnread={setShowOnlyUnread}
                      />
                    )}
                    {isAllDataAvailable && reportSequence.teams && (
                      <ConslusionTeams
                        metadata={reportSequence.metadata}
                        rootTeamsStatistics={reportSequence.rootTeamsStatistics!}
                        teams={reportSequence.teams}
                        reportRoots={reportSequence.reportRoots}
                      />
                    )}
                    <hr className={'mb-8'} />
                    <Contacts adminEmails={reportSequence.metadata.adminEmails!} id={'contacts'} />
                  </div>
                </section>
              ) : (
                <ArchivedSection className={'container container--md'} data-cy="report-archived-label">
                  {t('reportArchived')}
                </ArchivedSection>
              )}
              <Footer />
              <PageTitleAndFavicon title={topicGroupTranslation.value} />
            </>
          </ReportContainer>
        </AggregatedReportDataContext.Provider>
      </SideMenuContextProvider>
    </ReportGTMContext.Provider>
  );
};

export default ReportSequence;
